import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, GalleryThumbnails, Palette, PencilRuler, ScanEye, Sparkles } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, A11y, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import bwPreview from '@/assets/img/30.jpg';
import scifiPreview from '@/assets/img/67.jpg';
import photoPreview from '@/assets/img/22.jpg';
import popPreview from '@/assets/img/6.jpg';
import drawingPreview from '@/assets/img/49.jpg';
import sculpturePreview from '@/assets/img/57.jpg';

const categories = [
  { title: "Black & White", description: "Timeless monochrome photography.", slug: "black-and-white", icon: <ScanEye size={24} />, image: bwPreview },
  { title: "Sci-Fi Visions", description: "Futuristic landscapes & alien worlds.", slug: "sci-fi", icon: <Sparkles size={24} />, image: scifiPreview },
  { title: "Photorealism", description: "Paintings that challenge perception.", slug: "photorealism", icon: <GalleryThumbnails size={24} />, image: photoPreview },
  { title: "Pop Art", description: "Bold colors from modern culture.", slug: "pop-art", icon: <Palette size={24} />, image: popPreview },
  { title: "Drawings", description: "Works in pencil, ink and charcoal.", slug: "drawings", icon: <PencilRuler size={24} />, image: drawingPreview },
  { title: "Sculptures", description: "Three-dimensional forms.", slug: "sculptures", icon: <Sparkles size={24} />, image: sculpturePreview },
];

const IndexPage = () => {
  useEffect(() => {
    const galleryItems = document.querySelectorAll('.gallery-item');
    galleryItems.forEach(item => {
      const inner = item.querySelector('.gallery-item-inner');
      if (!inner) return;

      const handleMouseMove = (e) => {
        const rect = item.getBoundingClientRect();
        const x = e.clientX - rect.left, y = e.clientY - rect.top;
        const centerX = rect.width / 2, centerY = rect.height / 2;
        const rotateX = (y - centerY) / 20, rotateY = (centerX - x) / 20;
        inner.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.1)`;
      };

      const handleMouseLeave = () => {
        inner.style.transform = 'rotateX(0deg) rotateY(0deg) scale(1)';
      };

      item.addEventListener('mousemove', handleMouseMove);
      item.addEventListener('mouseleave', handleMouseLeave);
      
      return () => {
        item.removeEventListener('mousemove', handleMouseMove);
        item.removeEventListener('mouseleave', handleMouseLeave);
      };
    });
  }, []);

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-24"
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium hero-title mb-6">
            Contemporary <span className="highlight">Art</span> Collections
          </h1>
          <p className="text-lg md:text-xl hero-paragraph max-w-3xl mx-auto">
            Explore our curated selection of modern artworks across six distinctive categories, each representing unique artistic visions.
          </p>
        </motion.div>

        <div className="category-carousel-container">
          <Swiper
            modules={[Pagination, A11y, Autoplay]}
            spaceBetween={30}
            slidesPerView={1}
            pagination={{ clickable: true }}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            className="category-carousel"
            breakpoints={{ 640: { slidesPerView: 2, spaceBetween: 30 }, 1024: { slidesPerView: 3, spaceBetween: 40 } }}
          >
            {categories.map((category) => (
              <SwiperSlide key={category.slug}>
                <div className="group category-card">
                  <div className="relative h-64 overflow-hidden rounded-t-lg">
                    <img src={category.image} alt={category.title} className="category-card-image" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center mb-3">
                       <div className="p-2 card-icon-wrapper rounded-md mr-4">
                         {category.icon}
                       </div>
                       <h2 className="text-xl font-serif font-medium card-title">{category.title}</h2>
                    </div>
                    <p className="text-sm card-description mb-4">{category.description}</p>
                    <a href={`portfolio/${category.slug}`} className="inline-flex items-center font-semibold text-sm card-link">
                      View Collection
                      <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" size={16} />
                    </a>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-24"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-medium hero-title mb-4">
              Featured <span className="highlight">Highlights</span>
            </h2>
            <p className="text-lg hero-paragraph max-w-3xl mx-auto">
              A glimpse into our most celebrated pieces across all collections.
            </p>
          </div>
          
          <div className="highlights-gallery">
            {[bwPreview, scifiPreview, photoPreview, popPreview, drawingPreview].map((img, i) => (
              <div key={i} className="gallery-item">
                <img src={img} alt={`Gallery highlight ${i + 1}`} className="gallery-item-inner" />
              </div>
            ))}
          </div>
            
          <div className="mt-12 text-center">
            {/* === THIS IS THE CLEANED-UP BUTTON === */}
            <a href="/gallery" className="monochrome-button">
              Explore Full Gallery
              <ArrowRight className="ml-2" />
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default IndexPage;